import React, { CSSProperties, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useTimer } from '@utils/hooks';

import { useCountDownNotificationContext } from '@containers/App/routes/private/CountDownNotificationContext';

import style from './CountDownNotification.scss';

const cn = bemCssModules(style, 'CountDownNotification');

export type TCountDownNotification = {
  seconds?: number;
};

const CountDownNotification: CustomFC<TCountDownNotification> = ({ seconds = 5, className }) => {
  const [{ onTimeExpired, onCancel, text, id }] = useCountDownNotificationContext();

  const { t } = useTranslation();

  const { counter, reset, restart } = useTimer({
    initialCounter: seconds,
    startOnMount: false,
  });

  useEffect(() => {
    if (counter <= 0) {
      onTimeExpired?.();
      reset();
    }
  }, [counter]);

  const size = 30;
  const radius = size / 2;
  const length = Math.PI * 2 * (radius + size * 0.1);

  const handleCancel = () => {
    onCancel?.();
    reset();
  };

  useEffect(() => {
    if (id) restart();

    return () => reset();
  }, [id]);

  if (!id) return null;

  return (
    <div className={cn(null, [className])} key={id}>
      <div className={cn('countdown')}>
        <div className={cn('number')}>{counter}</div>
        <svg className={cn('svg')}>
          <circle
            className={cn('circle')}
            r={radius - 1}
            cx={radius}
            cy={radius}
            style={{ '--duration': `${seconds}s`, '--dash-array': length } as CSSProperties}
          />
        </svg>
      </div>
      <div className={cn('text')}>{text}</div>
      <button className={cn('cancel')} type="button" onClick={handleCancel}>
        {t('notifications.cancel')}
      </button>
    </div>
  );
};

export default CountDownNotification;
